// functions/intranet/src/lib/textFold.js
// ──────────────────────────────────────────────────────────────────────────────
// Search-text folding for free-text school/address lookups: casefold, ß→ss,
// ä/ö/ü→ae/oe/ue, remaining diacritics stripped, Straße/Strasse/Str./Str→one
// token, punctuation stripped, whitespace collapsed. Mirrors the folding rules
// already used by hrDashboard/schoolMatch.js's normSchule() so the same text
// folds the same way wherever a search box and the matcher meet.
//
// Plain ESM, NO imports — the frontend bundles/imports this file directly with
// zero dependencies (e.g. a live school-search filter).
//
// NOT reused by ./schoolMatch.js: that file's own fold family (foldName/
// foldCompact/foldCore/foldStrasse) collapses umlauts to their bare base letter
// (ä→a, via NFKD-strip) rather than the ae/oe/ue digraphs used here. Switching
// schoolMatch.js to this folding would shift the key space and levenshtein
// similarity ratios for every umlaut-bearing school name its matcher already
// relies on, so it keeps its own helpers untouched — see the note there.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * @param {string} s
 * @returns {string}
 */
export function foldSearchText(s) {
  let x = String(s ?? '').trim().toLowerCase()
  if (!x) return ''
  x = x.replace(/ß/g, 'ss').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
  x = x.normalize('NFKD').replace(/[̀-ͯ]/g, '') // strip remaining diacritics (é, à, ñ, …)
  x = x.replace(/strasse|str\.|str\b/g, 'strasse')         // straße/strasse/str./str → one token
  x = x.replace(/[^a-z0-9 ]/g, ' ')
  return x.replace(/\s+/g, ' ').trim()
}

/**
 * Same folding, but umlauts collapse to their BARE base letter (ä→a) the way
 * ./schoolMatch.js's fold family does, instead of the ae/oe/ue digraphs above.
 * @param {string} s
 * @returns {string}
 */
export function foldSearchTextBase(s) {
  let x = String(s ?? '').trim().toLowerCase()
  if (!x) return ''
  x = x.replace(/ß/g, 'ss')
  x = x.normalize('NFKD').replace(/[̀-ͯ]/g, '')   // ä→a, é→e, ñ→n, …
  x = x.replace(/strasse|str\.|str\b/g, 'strasse')
  x = x.replace(/[^a-z0-9 ]/g, ' ')
  return x.replace(/\s+/g, ' ').trim()
}

/**
 * Substring search that hits under EITHER folding, so a search box agrees with
 * the matcher no matter which convention the typist or the data uses:
 * "Grumbrecht" finds "Grümbrechtstrasse" (bare fold) and "Mueller" finds
 * "Müller" (digraph fold). Matching only one way silently loses the other.
 *
 * @param {string} haystack
 * @param {string} needle
 * @returns {boolean}
 */
export function searchMatches(haystack, needle) {
  const n = foldSearchText(needle)
  if (!n) return true
  if (foldSearchText(haystack).includes(n)) return true
  return foldSearchTextBase(haystack).includes(foldSearchTextBase(needle))
}
